import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { v4 } from 'uuid'
import {
  defaultFavoriteSetId,
  type FavoriteEntryId,
  type FavoriteSet,
  type FavoriteSetId,
  type FavoriteSets,
  type NewFavoriteSet,
  type UpdateFavoriteSet,
} from '#features/FavoriteSets/FavoriteSet.ts'

export type FavoritesState = {
  favoriteSets: FavoriteSets
  activeFavoriteSetId: FavoriteSetId
}

export const initialFavoritesState: FavoritesState = {
  favoriteSets: [{ id: defaultFavoriteSetId, name: 'My Favorites', createdAt: Date.now(), entries: [] }],
  activeFavoriteSetId: defaultFavoriteSetId,
}

interface SetActiveFavoriteSetActionPayload {
  id: FavoriteSetId
}

type AddFavoriteSetActionPayload = NewFavoriteSet
type UpdateFavoriteSetActionPayload = UpdateFavoriteSet

interface RemoveFavoriteSetActionPayload {
  id: FavoriteSetId
}

interface AddFavoriteActionPayload {
  favoriteSetId?: FavoriteSetId
  entryId: FavoriteEntryId
}

interface AddFavoritesActionPayload {
  favoriteSetId?: FavoriteSetId
  entryIds: FavoriteEntryId[]
}

interface RemoveFavoriteActionPayload {
  favoriteSetId?: FavoriteSetId
  entryId: FavoriteEntryId
}

interface ToggleFavoriteActionPayload {
  favoriteSetId?: FavoriteSetId
  entryId: FavoriteEntryId
}

export const favoritesSlice = createSlice({
  name: 'favorites',
  initialState: initialFavoritesState,
  reducers: {
    addFavoriteSet: (state, { payload }: PayloadAction<AddFavoriteSetActionPayload>) => {
      const id = payload.id ?? (v4() as FavoriteSetId)

      if (state.favoriteSets.find((set) => set.id === id)) {
        throw new Error(`Favorite set with id already exists: ${id}`)
      }

      const newSet: FavoriteSet = {
        id,
        createdAt: Date.now(),
        entries: payload.entries ?? [],
        ...payload,
      }
      state.favoriteSets.push(newSet)
    },
    updateFavoriteSet: (state, { payload }: PayloadAction<UpdateFavoriteSetActionPayload>) => {
      const existingSetIndex = state.favoriteSets.findIndex((set) => set.id === payload.id)
      if (existingSetIndex === -1) {
        throw new Error(`Favorite set not found: ${payload.id}`)
      }
      state.favoriteSets[existingSetIndex] = {
        ...state.favoriteSets[existingSetIndex],
        ...payload,
      }
    },
    removeFavoriteSet: (state, { payload }: PayloadAction<RemoveFavoriteSetActionPayload>) => {
      if (state.favoriteSets.length === 1) {
        throw new Error('Cannot remove the last favorite set')
      }

      const setIndex = state.favoriteSets.findIndex((set) => set.id === payload.id)
      if (setIndex === -1) {
        throw new Error(`Favorite set not found: ${payload.id}`)
      }
      state.favoriteSets.splice(setIndex, 1)
      if (state.activeFavoriteSetId === payload.id) {
        state.activeFavoriteSetId = state.favoriteSets[0].id
      }
    },
    setActiveFavoriteSet: (state, { payload }: PayloadAction<SetActiveFavoriteSetActionPayload>) => {
      const existingSet = state.favoriteSets.find((set) => set.id === payload.id)
      if (!existingSet) {
        throw new Error(`Favorite set not found: ${payload.id}`)
      }
      state.activeFavoriteSetId = payload.id
    },
    addFavorite: (state, { payload }: PayloadAction<AddFavoriteActionPayload>) => {
      const favoriteSetId = payload.favoriteSetId ?? state.activeFavoriteSetId
      const favoriteSet = state.favoriteSets.find((set) => set.id === favoriteSetId)
      if (!favoriteSet) {
        throw new Error(`Favorite set not found: ${favoriteSetId}`)
      }
      const existingEntry = favoriteSet.entries.find((entry) => entry.id === payload.entryId)
      if (existingEntry) {
        return
      }
      favoriteSet.entries.push({ id: payload.entryId, createdAt: Date.now() })
    },
    addFavorites: (state, { payload }: PayloadAction<AddFavoritesActionPayload>) => {
      const favoriteSetId = payload.favoriteSetId ?? state.activeFavoriteSetId
      const favoriteSet = state.favoriteSets.find((set) => set.id === favoriteSetId)
      if (!favoriteSet) {
        throw new Error(`Favorite set not found: ${favoriteSetId}`)
      }
      const existingEntryIds = new Set(favoriteSet.entries.map((entry) => entry.id))
      const newEntries = payload.entryIds.filter((id) => !existingEntryIds.has(id)).map((id) => ({ id, createdAt: Date.now() }))
      favoriteSet.entries.push(...newEntries)
    },
    removeFavorite: (state, { payload }: PayloadAction<RemoveFavoriteActionPayload>) => {
      const favoriteSetId = payload.favoriteSetId ?? state.activeFavoriteSetId
      const favoriteSet = state.favoriteSets.find((set) => set.id === favoriteSetId)
      if (!favoriteSet) {
        throw new Error(`Favorite set not found: ${favoriteSetId}`)
      }
      const entryIndex = favoriteSet.entries.findIndex((entry) => entry.id === payload.entryId)
      if (entryIndex === -1) {
        return
      }
      favoriteSet.entries.splice(entryIndex, 1)
    },
    toggleFavorite: (state, { payload }: PayloadAction<ToggleFavoriteActionPayload>) => {
      const favoriteSetId = payload.favoriteSetId ?? state.activeFavoriteSetId
      const favoriteSet = state.favoriteSets.find((set) => set.id === favoriteSetId)
      if (!favoriteSet) {
        throw new Error(`Favorite set not found: ${favoriteSetId}`)
      }
      const existingEntry = favoriteSet.entries.find((entry) => entry.id === payload.entryId)
      if (existingEntry) {
        favoriteSet.entries = favoriteSet.entries.filter((entry) => entry.id !== payload.entryId)
      } else {
        favoriteSet.entries.push({ id: payload.entryId, createdAt: Date.now() })
      }
    },
  },
})

export function makeFavoriteIdsSet(favorites: FavoritesState): Set<FavoriteEntryId> {
  const activeSet = favorites.favoriteSets.find((set) => set.id === favorites.activeFavoriteSetId)
  if (!activeSet) {
    return new Set()
  }
  return new Set(activeSet.entries.map((entry) => entry.id))
}
